import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormEvent, useState, useEffect } from "react";
import { useI18n } from "../../i18n/i18n";
import SalaryCharts from "../charts/SalaryCharts";
import CurrencySelector from "../CurrencySelector";
import { useAuthCheck } from "../../hooks/useAuthCheck";
import { useAuth } from "../AuthProvider";
import { AuthModal } from "../AuthModal";
import { generateSalaryPDF } from "../../lib/salaryPdfExport";
import { Download, Calculator, TrendingUp, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { LanguageLink } from "../LanguageLink";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";

// Import tax calculation modules
import {
  calculateTaxByCountry,
  SUPPORTED_COUNTRIES,
} from "./net-salary";
import type { SalaryCalculatorState, SalaryResults } from "./net-salary";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NetSalaryCalculator = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const {
    checkAuthAndExecute,
    isCheckingAuth,
    showAuthModal,
    setShowAuthModal,
    pendingCallback,
  } = useAuthCheck();
  const [currency, setCurrency] = useState<string>("EUR");
  const [exchangeRates] = useState({
    EUR: 1,
    USD: 1.08,
    GBP: 0.85,
    CHF: 0.95,
  });

  const [formState, setFormState] = useState<SalaryCalculatorState>({
    // Professional Data
    grossIncome: 25500,
    numberOfPayments: "12",
    contractType: "permanent",
    professionalCategory: "general",
    geographicMobility: false,
    movedResidence: false,

    // Personal Data
    country: "Spain",
    age: 30,
    familyStatus: "single",
    hasDisability: false,
    disabilityDegree: 0,
    region: "Madrid",
    dependents: 0,
    churchMember: false,
    pensionContributions: 0,
    charitableDonations: 0,
  });

  const [results, setResults] = useState<SalaryResults | null>(null);

  const [chartData, setChartData] = useState({
    pieChartData: {
      labels: [
        t("salary.takeHomePay"),
        t("salary.taxes"),
        t("salary.socialSecurity"),
        t("salary.retirement"),
        t("salary.healthInsurance"),
        t("salary.deductions"),
      ],
      values: [0, 0, 0, 0, 0, 0],
    },
    barChartData: {
      labels: [t("salary.gross"), t("salary.net")],
      values: [0, 0],
    },
  });

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]:
        value === ""
          ? ""
          : name === "numberOfPayments"
            ? value
            : parseFloat(value),
    }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDependentsChange = (change: number) => {
    const newValue = Math.max(0, formState.dependents + change);
    setFormState((prev) => ({
      ...prev,
      dependents: newValue,
    }));
  };

  // ============================================================================
  // SALARY CALCULATION
  // ============================================================================

  const calculateSalary = () => {
    const {
      grossIncome,
      numberOfPayments,
      contractType,
      geographicMobility,
      movedResidence,
      country,
      age,
      familyStatus,
      hasDisability,
      disabilityDegree,
      dependents,
    } = formState;

    // Use unified tax calculation from modules
    const taxResult = calculateTaxByCountry({
      country,
      grossIncome,
      contractType,
      age,
      familyStatus,
      dependents,
      hasDisability,
      disabilityDegree,
      geographicMobility,
      movedResidence,
    });

    const taxDeduction = taxResult.tax;
    const taxRate = taxResult.taxRate;
    const socialSecurityDeduction = taxResult.socialSecurity;
    const allowances = taxResult.allowances || 0;
    const breakdown = taxResult.breakdown;

    // Calculate net annual income
    const netAnnual = grossIncome - taxDeduction - socialSecurityDeduction;

    // Calculate monthly amounts based on number of payments
    const numPayments = parseInt(numberOfPayments, 10) || 12;
    const grossMonthly = grossIncome / numPayments;
    const netMonthly = netAnnual / numPayments;

    const newResults: SalaryResults = {
      grossAnnual: grossIncome,
      grossMonthly,
      taxDeduction,
      taxRate,
      socialSecurityDeduction,
      retirementDeduction: 0,
      healthInsuranceDeduction: 0,
      otherDeductions: 0,
      allowances,
      netAnnual,
      netMonthly,
      breakdown,
    };

    setResults(newResults);
    updateChartData(newResults);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    calculateSalary();
  };

  // ============================================================================
  // CURRENCY HANDLING
  // ============================================================================

  const handleCurrencyChange = (newCurrency: string) => {
    if (newCurrency !== currency && results) {
      const conversionRate =
        exchangeRates[newCurrency as keyof typeof exchangeRates] /
        exchangeRates[currency as keyof typeof exchangeRates];

      const newResults: SalaryResults = {
        ...results,
        grossAnnual: results.grossAnnual * conversionRate,
        grossMonthly: results.grossMonthly * conversionRate,
        taxDeduction: results.taxDeduction * conversionRate,
        taxRate: results.taxRate,
        socialSecurityDeduction: results.socialSecurityDeduction * conversionRate,
        retirementDeduction: results.retirementDeduction * conversionRate,
        healthInsuranceDeduction: results.healthInsuranceDeduction * conversionRate,
        otherDeductions: results.otherDeductions * conversionRate,
        allowances: results.allowances * conversionRate,
        netAnnual: results.netAnnual * conversionRate,
        netMonthly: results.netMonthly * conversionRate,
      };

      setResults(newResults);
      updateChartData(newResults);

      setFormState((prev) => ({
        ...prev,
        grossIncome: prev.grossIncome * conversionRate,
      }));
    }

    setCurrency(newCurrency);
  };

  // When country changes, update the currency
  useEffect(() => {
    const countryCurrencyMap: Record<string, string> = {
      Spain: "EUR",
      USA: "USD",
      UK: "GBP",
      France: "EUR",
      Germany: "EUR",
      Italy: "EUR",
      Switzerland: "CHF",
    };

    const newCurrency = countryCurrencyMap[formState.country] || "EUR";
    if (newCurrency !== currency) {
      setCurrency(newCurrency);
    }
  }, [formState.country]);

  // ============================================================================
  // FORMATTING HELPERS
  // ============================================================================

  const formatCurrency = (amount: number) => {
    if (amount === undefined || amount === null) return "—";

    return new Intl.NumberFormat(
      currency === "USD"
        ? "en-US"
        : currency === "GBP"
          ? "en-GB"
          : currency === "CHF"
            ? "de-CH"
            : "de-DE",
      {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(amount);
  };

  const formatPercentage = (amount: number, total: number) => {
    if (amount === undefined || amount === null || total === 0) return "—";
    const percentage = (amount / total) * 100;
    return `${percentage.toFixed(1)}%`;
  };

  const updateChartData = (newResults: SalaryResults) => {
    setChartData({
      pieChartData: {
        labels: [
          t("tools.salary.takeHomePay"),
          t("tools.salary.taxes"),
          t("tools.salary.socialSecurity"),
          t("tools.salary.retirement"),
          t("tools.salary.deductions"),
        ],
        values: [
          newResults.netMonthly,
          newResults.taxDeduction / 12,
          newResults.socialSecurityDeduction / 12,
          newResults.retirementDeduction / 12,
          newResults.otherDeductions / 12,
        ],
      },
      barChartData: {
        labels: [t("tools.salary.gross"), t("tools.salary.net")],
        values: [newResults.grossAnnual, newResults.netAnnual],
      },
    });
  };

  // Determine which fields to show based on selected country
  const showField = (fieldName: string): boolean => {
    const countryFields: Record<string, string[]> = {
      Spain: [
        "numberOfPayments",
        "contractType",
        "geographicMobility",
        "movedResidence",
        "age",
        "familyStatus",
        "dependents",
        "hasDisability",
        "pensionContributions",
        "charitableDonations",
      ],
      USA: [
        "numberOfPayments",
        "familyStatus",
        "dependents",
        "hasDisability",
        "region",
        "pensionContributions",
        "charitableDonations",
      ],
      France: [
        "numberOfPayments",
        "professionalCategory",
        "age",
        "familyStatus",
        "dependents",
        "charitableDonations",
      ],
      Germany: [
        "numberOfPayments",
        "familyStatus",
        "dependents",
        "churchMember",
        "pensionContributions",
        "charitableDonations",
      ],
      UK: [
        "numberOfPayments",
        "familyStatus",
        "dependents",
        "hasDisability",
        "region",
        "pensionContributions",
        "charitableDonations",
      ],
      Italy: [
        "numberOfPayments",
        "familyStatus",
        "dependents",
        "region",
        "pensionContributions",
        "charitableDonations",
      ],
      Switzerland: [
        "numberOfPayments",
        "age",
        "familyStatus",
        "dependents",
        "hasDisability",
        "region",
        "churchMember",
        "pensionContributions",
        "charitableDonations",
      ],
    };

    return countryFields[formState.country]?.includes(fieldName) || false;
  };

  const handleExportResults = () => {
    const executeExport = () => {
      if (!results) return;

      const exportData = {
        ...formState,
        ...results,
      };

      const doc = generateSalaryPDF(exportData, currency, t);
      doc.save(
        `salary-calculation-${new Date().toISOString().split("T")[0]}.pdf`,
      );
    };

    checkAuthAndExecute(executeExport);
  };

  // Effect to execute pending callback after successful authentication
  useEffect(() => {
    if (user && pendingCallback && !showAuthModal) {
      pendingCallback();
    }
  }, [user, pendingCallback, showAuthModal]);

  return (
    <div className="container mx-auto px-4 md:px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-6">
        {t("tools.salary.title")}
      </h2>
        <p className="text-neutral-600 text-center mb-8">
          {t("tools.salary.description")}
        </p>

        <Card className="bg-neutral-100 rounded-xl mb-10">
          <CardContent className="pt-6">
            <div className="md:flex gap-6">
              <div className="md:w-7/12 space-y-4 mb-6 md:mb-0">
                <form onSubmit={handleSubmit}>
                  <Accordion
                    type="single"
                    collapsible
                    defaultValue="professional-data"
                  >
                    <AccordionItem value="professional-data">
                      <AccordionTrigger className="text-lg font-semibold">
                        {t("tools.salary.professionalData")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 mt-2">
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <Label htmlFor="grossIncome">
                                {t("tools.salary.grossIncome")} ({currency})
                              </Label>
                            </div>
                            <Input
                              id="grossIncome"
                              name="grossIncome"
                              type="number"
                              className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#EA580C]"
                              value={formState.grossIncome}
                              min="0"
                              onChange={handleInputChange}
                            />
                          </div>

                          {showField("numberOfPayments") && (
                            <div>
                              <Label htmlFor="numberOfPayments">
                                {t("tools.salary.numberOfPayments")}
                              </Label>
                              <Select
                                value={formState.numberOfPayments}
                                onValueChange={(value) =>
                                  handleSelectChange("numberOfPayments", value)
                                }
                              >
                                <SelectTrigger className="w-full" aria-label={t("tools.salary.numberOfPayments")}>
                                  <SelectValue placeholder="Select payments" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="12">
                                    {t("tools.salary.paymentOptions.monthly")}
                                  </SelectItem>
                                  <SelectItem value="14">
                                    {t("tools.salary.paymentOptions.withExtras")}
                                  </SelectItem>
                                  <SelectItem value="16">
                                    {t("tools.salary.paymentOptions.with16")}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {showField("contractType") && (
                            <div>
                              <Label htmlFor="contractType">
                                {t("tools.salary.contractType")}
                              </Label>
                              <Select
                                value={formState.contractType}
                                onValueChange={(value) =>
                                  handleSelectChange("contractType", value)
                                }
                              >
                                <SelectTrigger className="w-full" aria-label={t("tools.salary.contractType")}>
                                  <SelectValue placeholder="Select contract type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="permanent">
                                    {t("tools.salary.permanent")}
                                  </SelectItem>
                                  <SelectItem value="temporary">
                                    {t("tools.salary.temporary")}
                                  </SelectItem>
                                  <SelectItem value="training">
                                    {t("tools.salary.training")}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {showField("professionalCategory") && (
                            <div>
                              <Label htmlFor="professionalCategory">
                                {t("tools.salary.professionalCategory")}
                              </Label>
                              <Select
                                value={formState.professionalCategory}
                                onValueChange={(value) =>
                                  handleSelectChange(
                                    "professionalCategory",
                                    value,
                                  )
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="general">
                                    {t("tools.salary.general")}
                                  </SelectItem>
                                  {formState.country === "France" && (
                                    <>
                                      <SelectItem value="cadre">
                                        {t("tools.salary.cadre")}
                                      </SelectItem>
                                      <SelectItem value="non-cadre">
                                        {t("tools.salary.nonCadre")}
                                      </SelectItem>
                                    </>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {showField("geographicMobility") && (
                            <div className="flex items-center space-x-2 pt-2">
                              <Checkbox
                                id="geographicMobility"
                                checked={formState.geographicMobility}
                                onCheckedChange={(checked) =>
                                  handleCheckboxChange(
                                    "geographicMobility",
                                    checked as boolean,
                                  )
                                }
                              />
                              <Label
                                htmlFor="geographicMobility"
                                className="cursor-pointer"
                              >
                                {t("tools.salary.geographicMobility")}
                              </Label>
                            </div>
                          )}

                          {showField("movedResidence") &&
                            formState.geographicMobility && (
                              <div className="flex items-center space-x-2 pt-2">
                                <Checkbox
                                  id="movedResidence"
                                  checked={formState.movedResidence}
                                  onCheckedChange={(checked) =>
                                    handleCheckboxChange(
                                      "movedResidence",
                                      checked as boolean,
                                    )
                                  }
                                />
                                <Label
                                  htmlFor="movedResidence"
                                  className="cursor-pointer"
                                >
                                  {t("tools.salary.movedResidence")}
                                </Label>
                              </div>
                            )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="personal-data" className="mt-4">
                      <AccordionTrigger className="text-lg font-semibold">
                        {t("tools.salary.personalData")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 mt-2">
                          <div>
                            <Label htmlFor="country">
                              {t("tools.salary.country")}
                            </Label>
                            <Select
                              value={formState.country}
                              onValueChange={(value) =>
                                handleSelectChange("country", value)
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={t("tools.salary.country")}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Spain">
                                  {t("tools.salary.spain")}
                                </SelectItem>
                                <SelectItem value="USA">
                                  {t("tools.salary.usa")}
                                </SelectItem>
                                <SelectItem value="France">
                                  {t("tools.salary.france")}
                                </SelectItem>
                                <SelectItem value="Germany">
                                  {t("tools.salary.germany")}
                                </SelectItem>
                                <SelectItem value="UK">
                                  {t("tools.salary.uk")}
                                </SelectItem>
                                <SelectItem value="Italy">
                                  {t("tools.salary.italy")}
                                </SelectItem>
                                <SelectItem value="Switzerland">
                                  {t("tools.salary.switzerland")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {showField("age") && (
                            <div>
                              <Label htmlFor="age">
                                {t("tools.salary.age")}
                              </Label>
                              <Input
                                id="age"
                                name="age"
                                type="number"
                                className="calculator-input w-full"
                                value={formState.age}
                                min="18"
                                max="100"
                                onChange={handleInputChange}
                              />
                            </div>
                          )}

                          {showField("familyStatus") && (
                            <div>
                              <Label htmlFor="familyStatus">
                                {t("tools.salary.familyStatus")}
                              </Label>
                              <Select
                                value={formState.familyStatus}
                                onValueChange={(value) =>
                                  handleSelectChange("familyStatus", value)
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select family status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="single">
                                    {t("tools.salary.single")}
                                  </SelectItem>
                                  <SelectItem value="married">
                                    {t("tools.salary.married")}
                                  </SelectItem>
                                  {formState.country === "USA" && (
                                    <SelectItem value="head">
                                      {t("tools.salary.head")}
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {showField("hasDisability") && (
                            <div>
                              <div className="flex items-center space-x-2 pt-2">
                                <Checkbox
                                  id="hasDisability"
                                  checked={formState.hasDisability}
                                  onCheckedChange={(checked) =>
                                    handleCheckboxChange(
                                      "hasDisability",
                                      checked as boolean,
                                    )
                                  }
                                />
                                <Label
                                  htmlFor="hasDisability"
                                  className="cursor-pointer"
                                >
                                  {t("tools.salary.hasDisability")}
                                </Label>
                              </div>

                              {formState.hasDisability &&
                                formState.country === "Spain" && (
                                  <div className="mt-2">
                                    <Label htmlFor="disabilityDegree">
                                      {t("tools.salary.disabilityDegree")}
                                    </Label>
                                    <Input
                                      id="disabilityDegree"
                                      name="disabilityDegree"
                                      type="number"
                                      className="calculator-input w-full"
                                      value={formState.disabilityDegree}
                                      min="33"
                                      max="100"
                                      onChange={handleInputChange}
                                    />
                                  </div>
                                )}
                            </div>
                          )}

                          {showField("dependents") && (
                            <div>
                              <Label
                                htmlFor="dependents"
                                className="block mb-2"
                              >
                                {t("tools.salary.dependents")}
                              </Label>
                              <div className="flex items-center">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="rounded-r-none"
                                  onClick={() => handleDependentsChange(-1)}
                                  disabled={formState.dependents <= 0}
                                >
                                  −
                                </Button>
                                <Input
                                  id="dependents"
                                  name="dependents"
                                  type="number"
                                  className="rounded-none text-center w-16 border-x-0"
                                  value={formState.dependents}
                                  readOnly
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="rounded-l-none"
                                  onClick={() => handleDependentsChange(1)}
                                >
                                  +
                                </Button>
                              </div>
                            </div>
                          )}

                          {showField("region") && (
                            <div>
                              <Label htmlFor="region">
                                {formState.country === "USA"
                                  ? "State"
                                  : formState.country === "Switzerland"
                                    ? "Canton"
                                    : formState.country === "UK"
                                      ? "Region"
                                      : formState.country === "Spain"
                                        ? t("tools.salary.autonomousCommunity")
                                        : formState.country === "Italy"
                                          ? "Region"
                                          : "Region"}
                              </Label>
                              <Select
                                value={formState.region}
                                onValueChange={(value) =>
                                  handleSelectChange("region", value)
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select region" />
                                </SelectTrigger>
                                <SelectContent>
                                  {formState.country === "Spain" && (
                                    <>
                                      <SelectItem value="Madrid">
                                        Madrid
                                      </SelectItem>
                                      <SelectItem value="Catalonia">
                                        Catalonia
                                      </SelectItem>
                                      <SelectItem value="Valencia">
                                        Valencia
                                      </SelectItem>
                                      <SelectItem value="Andalusia">
                                        Andalusia
                                      </SelectItem>
                                    </>
                                  )}
                                  {formState.country === "USA" && (
                                    <>
                                      <SelectItem value="California">
                                        California
                                      </SelectItem>
                                      <SelectItem value="Texas">
                                        Texas
                                      </SelectItem>
                                      <SelectItem value="New York">
                                        New York
                                      </SelectItem>
                                      <SelectItem value="Florida">
                                        Florida
                                      </SelectItem>
                                    </>
                                  )}
                                  {formState.country === "UK" && (
                                    <>
                                      <SelectItem value="England">
                                        England
                                      </SelectItem>
                                      <SelectItem value="Scotland">
                                        Scotland
                                      </SelectItem>
                                      <SelectItem value="Wales">
                                        Wales
                                      </SelectItem>
                                      <SelectItem value="Northern Ireland">
                                        Northern Ireland
                                      </SelectItem>
                                    </>
                                  )}
                                  {formState.country === "Italy" && (
                                    <>
                                      <SelectItem value="Lombardy">
                                        Lombardy
                                      </SelectItem>
                                      <SelectItem value="Lazio">
                                        Lazio
                                      </SelectItem>
                                      <SelectItem value="Tuscany">
                                        Tuscany
                                      </SelectItem>
                                      <SelectItem value="Sicily">
                                        Sicily
                                      </SelectItem>
                                    </>
                                  )}
                                  {formState.country === "Switzerland" && (
                                    <>
                                      <SelectItem value="Zurich">
                                        Zurich
                                      </SelectItem>
                                      <SelectItem value="Geneva">
                                        Geneva
                                      </SelectItem>
                                      <SelectItem value="Bern">Bern</SelectItem>
                                      <SelectItem value="Ticino">
                                        Ticino
                                      </SelectItem>
                                    </>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {showField("churchMember") && (
                            <div className="flex items-center space-x-2 pt-2">
                              <Checkbox
                                id="churchMember"
                                checked={formState.churchMember}
                                onCheckedChange={(checked) =>
                                  handleCheckboxChange(
                                    "churchMember",
                                    checked as boolean,
                                  )
                                }
                              />
                              <Label
                                htmlFor="churchMember"
                                className="cursor-pointer"
                              >
                                {t("tools.salary.churchMember")}
                              </Label>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem
                      value="additional-deductions"
                      className="mt-4"
                    >
                      <AccordionTrigger className="text-lg font-semibold">
                        {t("tools.salary.additionalDeductions")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 mt-2">
                          {showField("pensionContributions") && (
                            <div>
                              <Label htmlFor="pensionContributions">
                                {t("tools.salary.pensionContributions")} (
                                {currency})
                              </Label>
                              <Input
                                id="pensionContributions"
                                name="pensionContributions"
                                type="number"
                                className="calculator-input w-full"
                                value={formState.pensionContributions}
                                min="0"
                                max={
                                  formState.country === "Spain"
                                    ? "1500"
                                    : formState.country === "UK"
                                      ? "60000"
                                      : "5000"
                                }
                                onChange={handleInputChange}
                              />
                              <p className="text-xs text-neutral-500 mt-1">
                                {formState.country === "Spain"
                                  ? t("tools.salary.maxDeductibleSpain")
                                  : formState.country === "UK"
                                    ? t("tools.salary.maxDeductibleUK")
                                    : formState.country === "Switzerland"
                                      ? t(
                                          "tools.salary.maxDeductibleSwitzerland",
                                        )
                                      : formState.country === "Italy"
                                        ? t("tools.salary.maxDeductibleItaly")
                                        : t(
                                            "tools.salary.retirementContributions",
                                          )}
                              </p>
                            </div>
                          )}

                          {showField("charitableDonations") && (
                            <div>
                              <Label htmlFor="charitableDonations">
                                {t("tools.salary.charitableDonations")} (
                                {currency})
                              </Label>
                              <Input
                                id="charitableDonations"
                                name="charitableDonations"
                                type="number"
                                className="calculator-input w-full"
                                value={formState.charitableDonations}
                                min="0"
                                onChange={handleInputChange}
                              />
                              <p className="text-xs text-neutral-500 mt-1">
                                {formState.country === "Spain" ||
                                formState.country === "Italy"
                                  ? t("tools.salary.deductibleUpTo10Percent")
                                  : formState.country === "France"
                                    ? t("tools.salary.deductible66Percent")
                                    : formState.country === "Switzerland"
                                      ? t(
                                          "tools.salary.deductibleUpTo20Percent",
                                        )
                                      : t("tools.salary.taxDeductionApplies")}
                              </p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Button
                    type="submit"
                    className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium mt-6"
                  >
                    {t("tools.salary.calculateNet")}
                  </Button>
                </form>
              </div>

              <div className="md:w-5/12">
                <Card className="bg-white p-5 rounded-lg border border-neutral-200 shadow-sm">
                  <h3 className="font-semibold text-lg mb-4 pb-2 border-b border-neutral-200">
                    {t("tools.salary.breakdown")}
                  </h3>

                  {!results ? (
                    <div className="space-y-3 opacity-60">
                      <div className="text-center py-6 text-neutral-500">
                        {t("tools.salary.calculateToSeeResults")}
                      </div>
                      <div className="flex justify-between items-end gap-4 mb-2">
                        <span className="text-neutral-600 text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.grossAnnual")}:
                        </span>
                        <span className="font-medium text-right whitespace-nowrap bg-neutral-100 px-4 py-1 rounded">
                          —
                        </span>
                      </div>
                      <div className="flex justify-between items-end gap-4 mb-2">
                        <span className="text-neutral-600 text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.grossMonthly")}:
                        </span>
                        <span className="font-medium text-right whitespace-nowrap bg-neutral-100 px-4 py-1 rounded">
                          —
                        </span>
                      </div>
                      <div className="flex justify-between items-end gap-4 mb-2 text-red-500">
                        <span className="text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.taxDeduction")}:
                        </span>
                        <span className="text-right whitespace-nowrap bg-neutral-100 px-4 py-1 rounded">
                          —
                        </span>
                      </div>
                      <div className="flex justify-between items-end gap-4 mb-2 text-red-500">
                        <span className="text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.socialSecurity")}:
                        </span>
                        <span className="text-right whitespace-nowrap bg-neutral-100 px-4 py-1 rounded">
                          —
                        </span>
                      </div>
                      <div className="flex justify-between items-end gap-4 pt-2 border-t border-neutral-200">
                        <span className="font-semibold text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.netAnnual")}:
                        </span>
                        <span className="font-bold text-right whitespace-nowrap bg-neutral-100 px-4 py-1 rounded">
                          —
                        </span>
                      </div>
                      <div className="flex justify-between items-end gap-4 pt-2 border-t border-neutral-200">
                        <span className="font-semibold text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.netMonthly")}:
                        </span>
                        <span className="font-bold text-xl text-right whitespace-nowrap bg-neutral-100 px-4 py-1 rounded">
                          —
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-end gap-4 mb-2">
                        <span className="text-neutral-600 text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.grossAnnual")}:
                        </span>
                        <span className="font-medium text-right whitespace-nowrap">
                          {formatCurrency(results.grossAnnual)}
                        </span>
                      </div>

                      <div className="flex justify-between items-end gap-4 mb-2">
                        <span className="text-neutral-600 text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.grossMonthly")}:
                        </span>
                        <span className="font-medium text-right whitespace-nowrap">
                          {formatCurrency(results.grossMonthly)}
                        </span>
                      </div>

                      {/* Show breakdown if available, otherwise show generic labels */}
                      {results.breakdown && results.breakdown.length > 0 ? (
                        <>
                          {results.breakdown.map((item) => (
                            <div 
                              key={item.id} 
                              className={`flex justify-between items-center gap-4 mb-2 ${
                                item.type === "tax" ? "text-red-500" : 
                                item.type === "social" ? "text-orange-500" : 
                                "text-blue-600"
                              }`}
                            >
                              <span className="text-wrap break-words flex-1 leading-tight">
                                {t(`tools.salary.taxConcepts.${item.id}`)}:
                              </span>
                              <div className="text-right">
                                <span className="whitespace-nowrap font-medium">
                                  -{formatCurrency(item.amount)}
                                </span>
                                {item.rate !== undefined && item.rate > 0 && (
                                  <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                                    item.type === "tax" ? "text-red-400 bg-red-50" : 
                                    item.type === "social" ? "text-orange-400 bg-orange-50" : 
                                    "text-blue-400 bg-blue-50"
                                  }`}>
                                    {item.rate.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center gap-4 mb-2 text-red-500">
                            <span className="text-wrap break-words flex-1 leading-tight">
                              {t("tools.salary.taxDeduction")}:
                            </span>
                            <div className="text-right">
                              <span className="whitespace-nowrap font-medium">
                                -{formatCurrency(results.taxDeduction)}
                              </span>
                              <span className="ml-2 text-xs text-red-400 bg-red-50 px-2 py-1 rounded-full">
                                {formatPercentage(results.taxDeduction, results.grossAnnual)}
                              </span>
                            </div>
                          </div>

                          <div className="flex justify-between items-center gap-4 mb-2 text-orange-500">
                            <span className="text-wrap break-words flex-1 leading-tight">
                              {t("tools.salary.socialSecurity")}:
                            </span>
                            <div className="text-right">
                              <span className="whitespace-nowrap font-medium">
                                -{formatCurrency(results.socialSecurityDeduction)}
                              </span>
                              <span className="ml-2 text-xs text-orange-400 bg-orange-50 px-2 py-1 rounded-full">
                                {formatPercentage(results.socialSecurityDeduction, results.grossAnnual)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}

                      {results.retirementDeduction > 0 && (
                        <div className="flex justify-between items-end gap-4 mb-2 text-blue-600">
                          <span className="text-wrap break-words flex-1 leading-tight">
                            {t("tools.salary.retirement")}:
                          </span>
                          <span className="text-right whitespace-nowrap">
                            {formatCurrency(results.retirementDeduction)}
                          </span>
                        </div>
                      )}

                      {results.otherDeductions > 0 && (
                        <div className="flex justify-between items-end gap-4 mb-2 text-blue-600">
                          <span className="text-wrap break-words flex-1 leading-tight">
                            {t("tools.salary.deductions")}:
                          </span>
                          <span className="text-right whitespace-nowrap">
                            {formatCurrency(results.otherDeductions)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between items-end gap-4 pt-2 border-t border-neutral-200">
                        <span className="font-semibold text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.netAnnual")}:
                        </span>
                        <span className="font-bold text-green-500 text-right whitespace-nowrap">
                          {formatCurrency(results.netAnnual)}
                        </span>
                      </div>

                      <div className="flex justify-between items-end gap-4 pt-2 border-t border-neutral-200">
                        <span className="font-semibold text-wrap break-words flex-1 leading-tight">
                          {t("tools.salary.netMonthly")}:
                        </span>
                        <span className="font-bold text-xl text-[#EA580C] text-right whitespace-nowrap">
                          {formatCurrency(results.netMonthly)}
                        </span>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {results ? (
            <SalaryCharts
              pieChartData={chartData.pieChartData}
              barChartData={chartData.barChartData}
              currency={currency}
            />
          ) : (
            <div className="md:col-span-2 text-center py-8 bg-neutral-100 rounded-lg opacity-60">
              <p className="text-neutral-500">
                {t("tools.salary.calculateToSeeCharts")}
              </p>
            </div>
          )}
        </div>

        <div className="text-center mb-10">
          <Button
            variant="ghost"
            className={`inline-flex items-center font-medium ${
              !results || isCheckingAuth
                ? "text-neutral-400 cursor-not-allowed"
                : "text-neutral-700 hover:text-[#EA580C] cursor-pointer"
            }`}
            onClick={handleExportResults}
            disabled={!results || isCheckingAuth}
          >
            <Download className="mr-1 h-4 w-4" />
            {isCheckingAuth ? t("common.loading") : t("common.exportResults")}
            {!user && (
              <span className="ml-1 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                {t("common.loginRequired")}
              </span>
            )}
          </Button>
        </div>

        {/* Educational Content Section */}
        <div className="mt-16 space-y-12">
          {/* Difference between net and gross salary */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salary.differenceTitle")}
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      Salario Bruto
                    </h3>
                    <p className="text-lg text-neutral-700 leading-relaxed">
                      {t("tools.salary.differenceGross")}
                    </p>
                  </div>
                </div>
                <p className="text-neutral-600 leading-relaxed ml-10">
                  {t("tools.salary.differenceGrossDetails")}
                </p>
              </div>

              <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
                <div className="flex items-center gap-4">
                  <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      Salario Neto
                    </h3>
                    <p className="text-lg text-neutral-700 leading-relaxed">
                      {t("tools.salary.differenceNet")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How to calculate net salary */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salary.howToCalculateTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.salary.howToCalculateIntro")}
              </p>
              <p className="text-lg text-neutral-800 font-medium leading-relaxed mb-8">
                {t("tools.salary.howToCalculateSteps")}
              </p>

              {/* Step 1: IRPF */}
              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200 mb-6">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salary.step1Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed mb-3">
                    {t("tools.salary.step1Desc")}
                  </p>
                  <p className="text-neutral-700 leading-relaxed mb-3">
                    {t("tools.salary.step1Example")}
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                    <p className="text-neutral-800">
                      {t("tools.salary.step1Myth")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2: Social Security */}
              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200 mb-6">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salary.step2Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed mb-3">
                    {t("tools.salary.step2Desc")}
                  </p>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
                    <p className="text-neutral-800">
                      {t("tools.salary.step2Note")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3: Additional deductions */}
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salary.step3Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salary.step3Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How to use calculator - Example */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salary.howToUseTitle")}
            </h2>
            <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 rounded-xl p-8 border border-slate-200">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.salary.howToUseIntro")}
              </p>

              <div className="bg-white rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">💵</span>
                    <span className="text-neutral-800 font-medium">
                      {t("tools.salary.exampleSalary")}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">📝</span>
                    <span className="text-neutral-800 font-medium">
                      {t("tools.salary.exampleContract")}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">🙍‍♂️</span>
                    <span className="text-neutral-800 font-medium">
                      {t("tools.salary.exampleStatus")}
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-neutral-800 font-medium leading-relaxed mb-6">
                {t("tools.salary.exampleStepsTitle")}
              </p>

              <div className="space-y-4">
                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">1.</span>
                    <div>
                      <h4 className="font-bold text-neutral-900 mb-1">
                        {t("tools.salary.exampleStep1Title")}
                      </h4>
                      <p className="text-neutral-700 text-sm leading-relaxed">
                        {t("tools.salary.exampleStep1Desc")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">2.</span>
                    <div>
                      <h4 className="font-bold text-neutral-900 mb-1">
                        {t("tools.salary.exampleStep2Title")}
                      </h4>
                      <p className="text-neutral-700 text-sm leading-relaxed">
                        {t("tools.salary.exampleStep2Desc")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">3.</span>
                    <div>
                      <h4 className="font-bold text-neutral-900 mb-1">
                        {t("tools.salary.exampleStep3Title")}
                      </h4>
                      <p className="text-neutral-700 text-sm leading-relaxed">
                        {t("tools.salary.exampleStep3Desc")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">4.</span>
                    <div>
                      <h4 className="font-bold text-neutral-900 mb-1">
                        {t("tools.salary.exampleStep4Title")}
                      </h4>
                      <p className="text-neutral-700 text-sm leading-relaxed">
                        {t("tools.salary.exampleStep4Desc")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How to increase net salary */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salary.increaseNetTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                {t("tools.salary.increaseNetIntro")}
              </p>

              {/* Increase salary section */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                  {t("tools.salary.increaseSalaryTitle")}
                </h3>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.salary.increaseSalaryIntro")}
                </p>
                <p className="text-neutral-800 font-medium mb-4">
                  {t("tools.salary.increaseSalarySubtitle")}
                </p>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <span className="text-xl font-bold text-green-600">1️⃣</span>
                    <p className="text-neutral-800 leading-relaxed">
                      {t("tools.salary.increaseSalaryOption1")}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                    <span className="text-xl font-bold text-blue-600">2️⃣</span>
                    <p className="text-neutral-800 leading-relaxed">
                      {t("tools.salary.increaseSalaryOption2")}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                    <span className="text-xl font-bold text-purple-600">3️⃣</span>
                    <p className="text-neutral-800 leading-relaxed">
                      {t("tools.salary.increaseSalaryOption3")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reduce taxes section */}
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                  {t("tools.salary.reduceTaxesTitle")}
                </h3>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.salary.reduceTaxesIntro")}
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.salary.reduceTaxesDesc")}
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.salary.reduceTaxesAdvantage")}
                </p>

                <div className="bg-gradient-to-r from-[#FF6600]/10 via-orange-50/50 to-[#FF6600]/10 rounded-lg p-6 border-l-4 border-[#FF6600] mb-6">
                  <p className="text-neutral-800 font-medium mb-4">
                    {t("tools.salary.reduceTaxesItems")}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="border border-neutral-200 rounded-lg p-4 hover:border-[#FF6600] transition-colors">
                    <h4 className="font-bold text-neutral-900 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#FF6600]" />
                      {t("tools.salary.taxTip1Title")}
                    </h4>
                    <p className="text-neutral-700 text-sm leading-relaxed">
                      {t("tools.salary.taxTip1Desc")}
                    </p>
                  </div>

                  <div className="border border-neutral-200 rounded-lg p-4 hover:border-[#FF6600] transition-colors">
                    <h4 className="font-bold text-neutral-900 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#FF6600]" />
                      {t("tools.salary.taxTip2Title")}
                    </h4>
                    <p className="text-neutral-700 text-sm leading-relaxed">
                      {t("tools.salary.taxTip2Desc")}
                    </p>
                  </div>

                  <div className="border border-neutral-200 rounded-lg p-4 hover:border-[#FF6600] transition-colors">
                    <h4 className="font-bold text-neutral-900 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#FF6600]" />
                      {t("tools.salary.taxTip3Title")}
                    </h4>
                    <p className="text-neutral-700 text-sm leading-relaxed">
                      {t("tools.salary.taxTip3Desc")}
                    </p>
                  </div>

                  <div className="border border-neutral-200 rounded-lg p-4 hover:border-[#FF6600] transition-colors">
                    <h4 className="font-bold text-neutral-900 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#FF6600]" />
                      {t("tools.salary.taxTip4Title")}
                    </h4>
                    <p className="text-neutral-700 text-sm leading-relaxed">
                      {t("tools.salary.taxTip4Desc")}
                    </p>
                  </div>

                  <div className="border border-neutral-200 rounded-lg p-4 hover:border-[#FF6600] transition-colors">
                    <h4 className="font-bold text-neutral-900 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#FF6600]" />
                      {t("tools.salary.taxTip5Title")}
                    </h4>
                    <p className="text-neutral-700 text-sm leading-relaxed">
                      {t("tools.salary.taxTip5Desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Practical tips */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salary.practicalTipsTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                {t("tools.salary.practicalTipsIntro")}
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.salary.tip1Title")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.salary.tip1Desc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">📈</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.salary.tip2Title")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.salary.tip2Desc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">📚</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.salary.tip3Title")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.salary.tip3Desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Content Section */}
        <div className="mt-16 space-y-12">
          {/* Other Calculators Section */}
          <div className="bg-gradient-to-br from-neutral-50 to-white rounded-xl p-8 border border-neutral-200">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.salary.otherCalculators")}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <LanguageLink
                to="/tools/compound-interest"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salary.compoundCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salary.compoundDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/mortgage-calculator"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salary.mortgageCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salary.mortgageDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/monthly-savings-calculator"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salary.monthlySavingsCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salary.monthlySavingsDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/salary-comparison"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salary.salaryComparisonCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salary.salaryComparisonDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border border-blue-200">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.salary.relatedArticles")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* First Article - Takes 2 columns */}
              <LanguageLink
                to="/resources/salary-negotiation-article"
                className="md:col-span-2 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=800&auto=format&fit=crop" 
                  alt="Professional salary negotiation meeting with business documents and handshake"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                      {t("tools.salary.article1Title")}
                    </h3>
                    <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                      {t("tools.salary.article1Desc")}
                    </p>
                    <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                      {t("tools.salary.readMore")}
                      <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </LanguageLink>

              {/* Second Article */}
              <LanguageLink
                to="/resources/irpf-guide-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=500&auto=format&fit=crop" 
                  alt="Tax deduction documents and calculator on desk"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.salary.article2Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.salary.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Third Article */}
              <LanguageLink
                to="/resources/first-salary-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?q=80&w=500&auto=format&fit=crop" 
                  alt="Optimizing salary and paycheck strategy with financial charts"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.salary.article3Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.salary.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Fourth Article - Spans remaining space */}
              <LanguageLink
                to="/resources/automate-finances-article"
                className="md:col-span-2 lg:col-span-4 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[200px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800&auto=format&fit=crop" 
                  alt="Personal financial planning with laptop showing budget and investment charts"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30 group-hover:from-black/80 group-hover:via-black/50 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-center items-start max-w-2xl">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                    {t("tools.salary.article4Title")}
                  </h3>
                  <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                    {t("tools.salary.article4Desc")}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.salary.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>
            </div>
          </div>
        </div>

        {/* Authentication Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode="login"
        />

        {/* Google Ad Placeholder */}
        {/* <AdPlaceholder className="mt-12" /> */}
      </div>
    </div>
  );
};

export default NetSalaryCalculator;